import React, { useMemo } from "react";
import { MainButton } from "./Buttons";
import { Avatar } from "primereact/avatar";
import Divider from "./Divider";
import CustomInputMask, { CustomCardInput, CustomIconFeild, CustomInputNumber } from "./Inputs";
import moment from "moment";
import { ReactComponent as ConfigureIcon } from "../assets/images/svg/configure.svg";
import AreaChart from "./Charts/AreaChart";
import { Skeleton } from "primereact/skeleton";
import { number } from "../utils/regex";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { ReactComponent as InfoIcon } from "../assets/images/svg/Info.svg";
import { ReactComponent as EditIcon } from "../assets/images/svg/pencil.svg"
import { ReactComponent as CreditCardIcon } from "../assets/images/svg/CreditCard.svg";

export function Row({ gap = 0, children, extraClass = "" }) {
  return (
    <div className={`flex flex-wrap gap-${gap} ${extraClass}`}>{children}</div>
  );
}

export function Col({
  lg = 4,
  md = 6,
  sm = 12,
  auto,
  extraClass = "",
  children,
}) {
  return (
    <div
      className={
        auto
          ? `col ${extraClass}`
          : `col-${sm} md:col-${md} lg:col-${lg} ${extraClass}`
      }
    >
      {children}
    </div>
  );
}

export function AlignContent({
  horizontal = "between",
  vertical = "center",
  children,
  wrap = false,
  extraClass = "",
}) {
  return (
    <div
      className={`flex align-items-${vertical} justify-content-${horizontal} ${wrap ? "flex-wrap" : ""
        } ${extraClass}`}
    >
      {children}
    </div>
  );
}

export function MainCard({
  width,
  title,
  subtitle,
  btnTitle,
  btnAction,
  titleLight,
  children,
  showCircleIcon,
  hideHeaderDivider,
  extraClass = "",
  enableButton = null,
  btnTextOnly,
  btnExtraClass,
  titleClass,
}) {
  //   return (
  //     <div className={`main-card relative ${width ? width : "col-12"} ${extraClass}`}>
  //       <div className={`p-2 h-full flex flex-column bg-surface-a ${extraClass}`} style={{ boxShadow: "4px 4px 10px 0 rgba(1,74,172,.18)", borderRadius: "20px", backgroundColor: "var(--surface-a)" }}>
  //         <AlignContent extraClass={`${!hideHeaderDivider && "border-bottom-1 border-gray-300"} m-2 pb-3`}>
  //   hideHeaderDivider,
  //   btnTextOnly,
  //   btnExtraClass,
  // }) {
  return (
    <div
      className={`main-card relative ${width ? width : "col-12"} ${extraClass}`}
    >
      <div className="p-2 border-round-lg bg-white shadow-1 h-full flex flex-column">
        <AlignContent
          extraClass={`${!hideHeaderDivider && "border-bottom-1 border-gray-300"
            } m-2 pb-3`}
        >
          <div>
            {title && (
              <h3
                className={`m-0 p-0 ${titleLight && "text-gray-500 text-base"
                  } ${titleClass}`}
              >
                {title}
                {showCircleIcon && (
                  <i
                    className={`pi pi-question-circle`}
                    style={{
                      cursor: "pointer",
                      fontSize: "1.2rem",
                      marginLeft: "1rem",
                    }}
                  ></i>
                )}
              </h3>
            )}
            {subtitle && <p className="mb-0 p-0">{subtitle}</p>}
          </div>
          {enableButton && (
            <Button
              icon="pi pi-check"
              label="Enabled"
              severity="help"
              outlined
            />
          )}
          {btnTitle && (
            <MainButton
              label={btnTitle}
              onClick={btnAction}
              textOnly={btnTextOnly}
              extraClass={btnExtraClass}
            />
          )}
        </AlignContent>
        <div className="main-card-content flex-auto">{children}</div>
      </div>
    </div>
  );
}

export function OverviewCard({
  title,
  value,
  icon,
  customIcon,
  iconClr = "primary",
  valueChanged,
  trendText = "since last month",
  width,
  isFlip,
  flipTitle,
  flipDescription,
  ifOrganization = true,
  onClick,
}) {
  return (
    <div
      className={`overview-card flex ${width ? width : "col-12 col-md-6 col-lg-3"
        }`}
      style={{ minWidth: "200px" }}
    >
      <div
        className={`p-4 align-items-stretch w-full relative ${isFlip ? "flip-card" : "card-inner"
          }`}
        style={{
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "6px",
          backgroundColor: "var(--surface-a)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div className="w-full h-full relative z-2">
          <p className="m-0 text-gray-500 text-base font-semibold">{title}</p>
          <h2
            className={`${title === "Failed" || title === "Alerts" ? "text-red-400" : ""
              } m-0 font-bold pt-3 text-2xl`}
          >
            {value}
          </h2>
          <p className="my-1 text-sm text-gray-600">
            <span
              className={`${valueChanged >= 0 ? "text-green-600" : "text-red-400"
                } font-semibold flex items-center gap-1`}
            >
              {valueChanged}
            </span>
          </p>
        </div>
        {isFlip && (
          <div className="w-full h-full border-round-lg card-back bg-white absolute left-0 top-0 z-4">
            <div
              className={`w-full h-full align-center ${iconClr ? "bg-" + iconClr + "-400" : "bg-primary"
                } `}
              onClick={onClick}
            >
              {" "}
              <div>
                <h3 className="m-0">{flipTitle}</h3>
                <p>{flipDescription}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProfileCard({
  width,
  name,
  organization,
  profile,
  img,
  address,
  phone,
  email,
}) {
  return (
    <div className={`main-card relative ${width ? width : "col-12"}`}>
      <div
        className="p-2 bg-surface-a"
        style={{
          boxShadow: "4px 4px 10px 0 rgba(1,74,172,.18)",
          borderRadius: "20px",
          backgroundColor: "#fff",
        }}
      >
        <AlignContent>
          <div>
            <h4 className="m-0">{name}</h4>
            <p className="m-0 text-primary">{organization}</p>
            <p className="mt-0">{profile}</p>
          </div>
          {img ? (
            <img src={img} alt={`${name}'s Profile`} />
          ) : (
            <Avatar
              icon="pi pi-user"
              className="mr-4"
              size="medium"
              shape="circle"
            />
          )}
        </AlignContent>
        {/* <p className="m-1">
          <i className="pi pi-map-marker mr-3 text-primary" />
          {address}
        </p>
        <p className="m-1">
          <i className="pi pi-phone mr-3 text-primary" />
          {phone}
        </p> */}
        <Divider extraClass="pt-0 pb-3" />
        <AlignContent wrap>
          <a
            href={`mailto:${email}`}
            className="no-underline text-primary col ellipse"
          >
            {email}
          </a>
          <i className="pi pi-envelope mr-2 col-1" />
        </AlignContent>
      </div>
    </div>
  );
}

export function ContentCard({
  title,
  isLoading,
  titleLight,
  children,
  searchValue,
  searchWidth,
  onSearch,
  primaryBtnAction,
  primaryBtnLabel,
  primaryBtnIcon,
  primaryIsDanger,
  primaryBtnDisable,
  secondaryBtnLabel,
  secondayBtnAction,
  secondaryBtnIcon,
  tertiaryBtnLabel,
  tertiaryBtnAction,
  tertiaryBtnIcon,
  extraClass = "",
}) {
  return (
    <div className={`content-card relative col ${extraClass}`}>
      <div
        className="p-3 h-full flex flex-column"
        style={{
          boxShadow: "4px 4px 10px 0 rgba(1,74,172,.18)",
          borderRadius: "20px",
          backgroundColor: "var(--surface-a)",
        }}
      >
        <AlignContent wrap extraClass="mb-3">
          {title && (
            <h3
              className={`my-2 p-0 ${titleLight && "text-gray-700 text-base"} ${tertiaryBtnLabel ? "col-4" : "col-6"
                }`}
            >
              {title}
            </h3>
          )}
          <AlignContent
            extraClass={
              title
                ? `md:col-${tertiaryBtnLabel ? "8" : "6"} col-12 p-0 gap-3`
                : "col-12 p-0 gap-3"
            }
          >
            {isLoading ? (
              <Skeleton height="2rem" />
            ) : (
              onSearch && (
                <CustomIconFeild
                  placeholder="Search..."
                  value={searchValue}
                  col={
                    title
                      ? "col p-0"
                      : searchWidth
                        ? searchWidth
                        : "max-w-22rem"
                  }
                  onChange={onSearch}
                  extraClass="py-75"
                />
              )
            )}
            <AlignContent horizontal="end" extraClass="gap-3">
              {secondayBtnAction && (
                <MainButton
                  label={secondaryBtnLabel}
                  icon={secondaryBtnIcon}
                  onClick={secondayBtnAction}
                  btnGray
                  isLoading={isLoading}
                />
              )}
              {primaryBtnAction && (
                <MainButton
                  label={primaryBtnLabel}
                  icon={primaryBtnIcon}
                  onClick={primaryBtnAction}
                  isDanger={primaryIsDanger}
                  disabled={primaryBtnDisable}
                  secondary
                  isLoading={isLoading}
                />
              )}
              {tertiaryBtnAction && (
                <MainButton
                  label={tertiaryBtnLabel}
                  icon={tertiaryBtnIcon}
                  onClick={tertiaryBtnAction}
                  secondary
                  isLoading={isLoading}
                />
              )}
            </AlignContent>
          </AlignContent>
        </AlignContent>
        {children}
      </div>
    </div>
  );
}

export function IntegrationCard({
  Icon,
  title,
  action,
  actionLabel,
  cardAction = () => null,
  children,
  connection,
}) {
  return (
    <div className="custom-card col bg-gray-900 m-2 mt-4" onClick={cardAction}>
      <AlignContent horizontal="left">
        {Icon && <Icon width={24} height={24} className="m-2" />}
        <h3 className="m-0">{title}</h3>
      </AlignContent>
      <div>{children}</div>
      <MainButton
        label={
          actionLabel ? (
            actionLabel
          ) : connection ? (
            <span>
              Configure Account <ConfigureIcon className="ml-2" />
            </span>
          ) : (
            "Connect account +"
          )
        }
        onClick={action}
        textOnly
        extraClass="p-2"
      />
    </div>
  );
}

export function RepoCard({
  Icon,
  name,
  isPrivate = true,
  updatedAt,
  connectAction,
  userRole,
}) {
  return (
    <AlignContent extraClass="w-full custom-card m-2 bg-gray-900">
      <AlignContent extraClass="gap-2 text-gray-500">
        {Icon && <Icon width={20} height={20} />}
        <span>{name}</span>
        <i className={`pi ${isPrivate ? "pi-lock" : "pi-lock-open"} mr-2`} />
        {updatedAt && (
          <>
            {" "}
            <i className="pi pi-circle-fill" style={{ fontSize: "5px" }} />
            <span>{moment(updatedAt).fromNow()}</span>
          </>
        )}
      </AlignContent>
      {userRole !== "viewer" && (
        <MainButton label="Connect" onRight onClick={connectAction} secondary />
      )}
    </AlignContent>
  );
}

export function OverviewChartCard({
  title = "",
  value = [],
  valueChanged = 0,
  valueChangedblack = 0,
  valueChangedUnit = "%",
  chartData,
  lineClr = "#417EF9",
  isLoading = false,
  badge = "",
}) {
  return isLoading ? (
    <div className="col">
      <Skeleton height="10rem" />{" "}
    </div>
  ) : (
    <div className="col-6 md:col">
      <div className="custom-card h-full">
        <AlignContent>
          <h4 className="text-gray-500 mb-0">{title}</h4>{" "}
          {badge && <p className="text-gray-500 mb-0">{badge}</p>}
        </AlignContent>
        <AlignContent wrap>
          <div className={chartData ? "md:col-3 col-12 min-w-max" : "col-12"}>
            <h2 className="p-0 m-0">{value}</h2>
            {valueChanged !== null ? (
              <p
                className={
                  valueChanged > 0
                    ? "text-green-500 my-1"
                    : valueChanged < 0
                      ? "text-red-500 my-1"
                      : "text-gray-500 my-1" || valueChangedblack
                        ? "my-1"
                        : number(valueChanged)
                          ? valueChanged > 0
                            ? "text-green-500 my-1"
                            : valueChanged < 0
                              ? "text-red-500 my-1"
                              : "text-gray-500 my-1"
                          : "text-gray-500 my-1"
                }
              >
                {valueChanged}
                {" " + valueChangedUnit}
              </p>
            ) : null}
          </div>
          {chartData && (
            <div className="col">
              <AreaChart
                height={100}
                lineClr={lineClr}
                lineOnly
                chartData={chartData}
              />
            </div>
          )}
        </AlignContent>
      </div>
    </div>
  );
}

export function AnalyticProgressCard({
  title,
  data,
  extraClass = "",
  key = "name",
  valueKey = "value",
  isLoading,
}) {
  const formatValue = (value) => {
    return value > 1000 ? (value / 1000).toFixed(2) + "K" : value;
  };

  const calculatePercentage = (value) => {
    let total = 0;
    data?.forEach((element) => {
      total += element[valueKey];
    });
    return (value / total) * 100;
  };

  return isLoading ? (
    <div className="col">
      <Skeleton height="10rem" />{" "}
    </div>
  ) : (
    <div className={`col custom-card p-0 ${extraClass}`}>
      <h4 className="text-gray-500 px-3">{title}</h4>
      <div className="m-height-15rem overflow-auto">
        {data?.map((item, index) => (
          <AlignContent
            key={index}
            extraClass="border-top-1 border-gray-300 px-2"
            wrap
          >
            <p className="w-12 md:w-6 white-space-nowrap overflow-hidden text-overflow-ellipsis">
              {item[key]}
            </p>
            <AlignContent extraClass="w-12 md:w-6">
              <p className="col-2 text-right">{formatValue(item[valueKey])}</p>
              <div className="col">
                <ProgressBar
                  value={calculatePercentage(item[valueKey])}
                  showValue={false}
                />
              </div>
            </AlignContent>
          </AlignContent>
        ))}
      </div>
    </div>
  );
}

export function RecentDeploymentCard({
  Icon,
  UserIcon,
  userName,
  projectName,
  cardAction = () => null,
  children,
}) {
  return (
    <div className="custom-card col bg-gray-900 m-2 py-4" onClick={cardAction}>
      <AlignContent horizontal="left">
        {UserIcon && <UserIcon width={24} height={24} className="m-2" />}
        <h3 className="m-0 text-gray-500 text-base font-semibold">
          {userName}
        </h3>
      </AlignContent>
      <AlignContent horizontal="between">
        <h3 className="m-0">{projectName}</h3>
        {Icon && <Icon width={40} height={40} className="m-2" />}
      </AlignContent>
      <div>{children}</div>
    </div>
  );
}
export function LanguageStackCard({
  title,
  description,
  customIcon,
  iconClr = "primary",
  onClickLearnMore,
  width,
  isLoading,
}) {
  return (
    <div
      className={`flex ${width ? width : "col-12 col-md-6 col-lg-3"}`}
      style={{ minWidth: "200px" }}
    >
      <div
        className="p-4 align-items-center border-round-lg shadow-md w-full bg-surface-a"
        style={{
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          backgroundColor: "var(--surface-a)",
        }}
      >
        <div>{customIcon(70, 70)}</div>
        <div className="w-full flex gap-3 items-center">
          <div className="mt-4 py-2">
            <h3>{title}</h3>
            <p className=" text-gray-500 text-lg">{description}</p>
          </div>
        </div>

        <div className="flex justify-start w-full pt-2">
          <MainButton
            label="Learn More"
            width="min-w-max"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={onClickLearnMore}
            isLoading={isLoading}
            className=""
            extraClass="bg-transparent text-blue-500 text-lg pl-2"
          />
        </div>
      </div>
    </div>
  );
}

export function SSOCard({ title, imageUrl, onClick, disabled }) {
  return (
    <div
      className="col-12 p-2"
      style={{
        height: "150px",
        width: "268px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1, // Set opacity based on disabled state
      }}
      onClick={!disabled ? onClick : undefined} // Disable click if disabled
    >
      <div
        className="p-4 border-1 surface-border surface-card border-round"
        style={{
          width: "268px",
          height: "150",
          borderRadius: "8px",
          border: "1px solid",
          padding: "16px 56px 16px 56px",
          gap: "12px",
        }}
      >
        <div className="flex align-items-center">
          <img
            src={imageUrl}
            alt={title}
            className="mb-3"
            style={{
              width: "143.09px",
              height: "43.15px",
              display: "block",
              margin: "0 auto",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function HistoryCard({
  title,
  dueDate,
  buttonLabel,
  cardAction = () => null,
}) {
  return (
    <div className="custom-card bg-yellow-100 m-2 px-4 border-1 border-yellow-200 border-round-xs flex justify-content-between align-items-center">
      <div className=" p-0">
        <div className="flex flex-column align-items-start justify-content-start flex-wrap">
          <h3 className="invoice-history-green-card m-0  p-0">{title}</h3>
          <div className=".invoice-grey-font pt-4 flex flex-column gap-2">
            <label className="text-sm m-0 p-0 font-normal text-grey-100">
              Due date :{dueDate}
            </label>
            <div className="flex gap-2 align-items-center">
              <label className="m-0">Status:</label>
              <span>
                <MainButton
                  label={buttonLabel}
                  onClick={cardAction}
                  textOnly
                  extraClass="border-1 border-yellow-400 p-1 border-round-md text-yellow-400"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className=" p-0">
        <div className="flex flex-column align-items-center">
          <h2 className="p-0 m-0 pb-3">$20</h2>
          <MainButton label="Pay Now" extraClass="border-round-sm" isSuccess />
        </div>
      </div>
    </div>
  );
}

export function NoDueCards({ noDueTitle }) {
  return (
    <div className="bg-green-50 m-2 px-4 border-1 border-green-300 border-round-xs  p-4">
      <h3 className="invoice-history-green-card text-center">{noDueTitle}</h3>
    </div>
  );
}
export function BiilingCard({
  title,
  used,
  total,
  icon,
  cardAction = () => null,
}) {
  const extractNumericValue = (value) => {
    const match = value.match(/[\d.]+/); // Match numbers (including decimals)
    return match ? parseFloat(match[0]) : 0; // Parse as float or fallback to 0
  };

  // Memoize the calculation to ensure it updates when props change
  const progressValue = useMemo(() => {
    const usedValue = extractNumericValue(used);
    const totalValue = extractNumericValue(total);

    // Calculate progress percentage
    return totalValue > 0 ? (usedValue / totalValue) * 100 : 0;
  }, [used, total]);
  return (
    <div className="custom-card bg-gray-900 py-3 border-1 border-gray-200 border-round-xs">
      <AlignContent horizontal="left" extraClass="mb-4">
        <h3 className="text-lato-custom m-0 text-gray-500 text-xs">{title}</h3>
      </AlignContent>
      <AlignContent horizontal="left">
        <h4 className="total-time text-base -mr-3 ">{used}</h4>
        <h4 className="spent-amount text-xs -mr-1 ">
          {" "}
          &nbsp;/ &nbsp;{total}
        </h4>
        <span className="flex items-center">
          <InfoIcon />
        </span>
      </AlignContent>
      <ProgressBar
        className="border-round-sm"
        value={progressValue}
        displayValueTemplate={() => ""}
      // displayValueTemplate={() => `${Math.round(progressValue)}%`}
      />
    </div>
  );
}

export const PaymentMethodCard = ({
  title = "Payment Method",
  description = "You will be charged for usage beyond your free limits. View pricing.", // Default description
  cardNumber = "",
  onEditClick,
  editButtonLabel = "Edit",
  extraClass = "",
}) => {
  return (
    <div
      className={`col-12 flex flex-wrap w-full pt-2 mt-1 mb-1 align-center ${extraClass}`}
    >
      <div className="col-12 sm:col-12 md:col-4 lg:col-4 xl:col-4 p-0 pr-3 block">
        <h4 className="m-0 p-0">{title}</h4>
        <label>{description}</label>
      </div>
      <div className="col-12 sm:col-12 md:col-8 lg:col-8 xl:col-8 p-0">
        <div className="border-1 border-round-sm border-gray-300 bg-gray-100 flex align-center justify-content-between gap-2 text-gray-400 mt-3 mb-3 p-2">
          <div className="align-center">
            <CreditCardIcon />
            <span>{cardNumber}</span>
          </div>
          <AlignContent horizontal="end">
            <MainButton
              label={editButtonLabel}
              icon={<EditIcon />}
              textOnly
              extraClass={`text-gray-400 py-0 ${extraClass}`}
              onClick={onEditClick}
            />
          </AlignContent>
        </div>
      </div>
    </div>
  );
};


